import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Typography } from '@/components/ui/typography';
import { IconComponent } from '../../../util/icons';
import rentvineLogo from '../../../asset/images/rentvine-logo.svg';
import buildiumLogo from '../../../asset/images/buildium-logo.svg';
import appfolioLogo from '../../../asset/images/appfolio-logo.svg';
import { dismissAlert, showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  PMS_SYNC_CACHE_KEY,
  useSyncAssetsMutation
} from '../services/assetApi';
import { orderApi } from '../../orders/services/orderApi';
import type { AssetSelection } from '../../../types/asset.types';
import {
  syncFailureFrom,
  type SyncFailure
} from '../failed-asset-sync/syncFailure';

export interface SyncAssetsProps {
  setShowSyncModel: (show: boolean) => void;
  /** Holds the failure the caller's FailedAssetSync notice shows; null clears it. */
  setSyncFailure: (failure: SyncFailure | null) => void;
  selection: AssetSelection;
  setShowSyncInProgress?: (show: boolean) => void;
  isSyncingFromAssets: boolean;
  /** Fires after a successful PMS sync so the caller can persist/mirror sync
      state (photoset badge + lastPmsSyncedAt). Optional: OrderCellAssets
      reflects sync state via the row indicator instead. */
  onSyncSuccess?: () => void;
  pmsSource: string;
  /** Photo count to show in the summary + CTA. Optional so callers outside
      the Sync modal (e.g. OrderCellAssets) can keep the generic
      "assets" label without threading a count through. */
  photoCount?: number;
  /** Whether a watermark is on file for the account this sync is for. The
   *  caller decides which account: the session's on /home, the property's on
   *  the internal workspace (DV-3345, as DownloadAsset since DV-2849 PR 2). */
  watermarkAvailable: boolean;
}

const SyncAssets = ({
  setShowSyncModel,
  setSyncFailure,
  selection,
  setShowSyncInProgress,
  isSyncingFromAssets,
  onSyncSuccess,
  pmsSource,
  photoCount,
  watermarkAvailable
}: SyncAssetsProps) => {
  const dispatch = useDispatch();
  const [isWatermark, setIsWatermark] = useState(false);
  // fixedCacheKey lets the assets page header observe this sync's in-flight
  // state (spinning callout icon) without prop threading.
  const [syncAssets] = useSyncAssetsMutation({
    fixedCacheKey: PMS_SYNC_CACHE_KEY
  });

  const handleSyncModalClose = () => {
    if (!isSyncingFromAssets) {
      setShowSyncInProgress?.(false);
    }
    setIsWatermark(false);
    setShowSyncModel(false);
  };

  const syncAssetAPICall = async () => {
    try {
      // No "sync started" toast: the header callout's spinning refresh icon
      // is the in-flight indicator; the only toasts are the outcome ones
      // (success below, failure via the FailedAssetSync modal).
      // Flip the in-progress spinner on as the request begins (not on icon
      // click) and close the options modal.
      if (!isSyncingFromAssets) {
        setShowSyncInProgress?.(true);
      }
      setShowSyncModel(false);
      await syncAssets({
        watermarked: isWatermark,
        propertyUUID: selection.propertyUUID,
        orderUUID: selection.orderUUID,
        photoUUIDs: selection.photoUUIDs
      }).unwrap();
      // The sync is synchronous backend-side: a 200 means assets were pushed to
      // the PMS and the order's `isAssetsSync` flag was persisted. Invalidate
      // the orders list (a separate RTK Query slice, so cross-slice util
      // dispatch) so the row's sync indicator reflects the new state.
      dispatch(orderApi.util.invalidateTags(['OrderList']));
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'Asset synced successfully!'
        })
      );
      onSyncSuccess?.();
      setSyncFailure(null);
      if (!isSyncingFromAssets) {
        setShowSyncInProgress?.(false);
      }
    } catch (error) {
      dispatch(dismissAlert());
      if (!isSyncingFromAssets) {
        setShowSyncInProgress?.(false);
      }
      setSyncFailure(syncFailureFrom(error));
      setShowSyncModel(false);
    }
  };

  const pmsLogo = (() => {
    const source = pmsSource?.toLowerCase();
    if (source === 'rentvine') return { src: rentvineLogo, alt: 'Rentvine' };
    if (source === 'buildium') return { src: buildiumLogo, alt: 'Buildium' };
    if (source === 'appfolio') return { src: appfolioLogo, alt: 'Appfolio' };
    return null;
  })();

  // Display label: prefer the explicit photoCount; fall back to a generic
  // wording for callers that don't pass it.
  const countLabel =
    typeof photoCount === 'number'
      ? `${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`
      : 'selected photos';

  // An explicit empty selection (e.g. only optimistic staged photos checked, DV-2252)
  // must not fire an unscoped sync.
  const isExplicitEmptySelection =
    selection.photoUUIDs !== undefined && selection.photoUUIDs.length === 0;

  return (
    <div className="flex flex-col gap-5">
      {/* Summary row: PMS logo + sync target description. Stacks on mobile
          so the text line gets the full content width to wrap into. */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
        {pmsLogo ? (
          <div className="flex h-10 shrink-0 items-center">
            <IconComponent src={pmsLogo.src} alt={pmsLogo.alt} />
          </div>
        ) : (
          <Typography variant="heading-md" className="shrink-0">
            {pmsSource}
          </Typography>
        )}
        <div className="flex flex-col gap-0.5 min-w-0">
          <Typography variant="body-semibold">
            Syncing {countLabel} at Medium quality
          </Typography>
          <Typography variant="small" className="text-muted-foreground">
            1920 × 1280 px — optimized for MLS systems
          </Typography>
        </div>
      </div>

      <div className="border-t border-border" />

      <div>
        <div className="flex items-center gap-3">
          <Typography variant="body-semibold">Watermark</Typography>
          <Switch
            checked={isWatermark}
            onCheckedChange={setIsWatermark}
            disabled={!watermarkAvailable}
            aria-label="Apply Watermark"
          />
        </div>
        <Typography variant="small" className="mt-2 text-muted-foreground">
          Apply your custom watermark to protect against fraud and maximize
          brand exposure. Don't have one yet? Email{' '}
          <a
            href="mailto:support@planomatic.com"
            className="text-primary underline"
          >
            support@planomatic.com
          </a>{' '}
          to get started.
        </Typography>
        {!watermarkAvailable && (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-info-soft px-3 py-2.5 text-sm text-info-soft-foreground">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>No watermark image is on file for your account.</span>
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={handleSyncModalClose}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={syncAssetAPICall}
          disabled={isExplicitEmptySelection}
          className="w-full sm:w-auto"
        >
          Sync {countLabel}
        </Button>
      </div>
    </div>
  );
};

export default SyncAssets;
